import { motion } from "framer-motion";
import { Check } from "lucide-react";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";

interface SuccessCheckmarkProps {
  className?: string;
}

/**
 * Drop-in replacement for a bare <Check> icon at every "success" moment
 * across the site (form submissions, copy-to-clipboard confirmations) —
 * plays a spring scale-in plus a brief expanding ring burst each time it
 * mounts, so a toggled `copied`-style confirmation replays the animation on
 * every re-trigger, not just the first. Uses `currentColor` for the ring so
 * it inherits whatever text color each call site already sets, rather than
 * introducing a new hardcoded color.
 */
export default function SuccessCheckmark({ className = "h-4 w-4" }: SuccessCheckmarkProps) {
  const reducedMotion = usePrefersReducedMotion();

  if (reducedMotion) {
    return <Check className={className} />;
  }

  return (
    <span className="relative inline-flex">
      <motion.span
        aria-hidden="true"
        initial={{ scale: 0.5, opacity: 0.6 }}
        animate={{ scale: 1.8, opacity: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="absolute inset-0 rounded-full bg-current"
      />
      <motion.span
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 500, damping: 20 }}
      >
        <Check className={className} />
      </motion.span>
    </span>
  );
}
